import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { OrderDetailPanel, type ViewConversationOptions } from './order-detail-panel';
import type { OrderPaymentActions } from './use-order-payment-actions';
import type { Order, OrderStatus } from '@/lib/orders-client';

interface OrderDetailDialogProps {
    /** Open while non-null. */
    order: Order | null;
    onClose: () => void;
    onStatusChange: (orderId: string, newStatus: OrderStatus) => void;
    onViewConversation?: (conversationId: string, options?: ViewConversationOptions) => void;
    onRetryPosPush?: (orderId: string) => Promise<void>;
    paymentActions?: OrderPaymentActions;
    /** A held storefront transfer draft: confirm its transfer by hand and place it. See the panel. */
    onPlaceHeldTransfer?: () => void;
    placingHeldTransfer?: boolean;
}

/**
 * The orders-page detail panel as a modal, flush against the dialog frame: the panel brings its
 * own header, title and close button, so the dialog contributes nothing but the frame and the
 * scroll bounds (hence `p-0`, `gap-0` and no second close button).
 */
export function OrderDetailDialog({
    order,
    onClose,
    onStatusChange,
    onViewConversation,
    onRetryPosPush,
    paymentActions,
    onPlaceHeldTransfer,
    placingHeldTransfer,
}: OrderDetailDialogProps) {
    const { t } = useTranslation('orders');

    return (
        <Dialog
            open={!!order}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <DialogContent
                showCloseButton={false}
                className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
            >
                {/* The panel's own header carries the visible title. */}
                <DialogHeader className="sr-only">
                    <DialogTitle>{t('detail.title', { id: order?.id.slice(0, 8) ?? '' })}</DialogTitle>
                </DialogHeader>
                {order && (
                    <OrderDetailPanel
                        embedded
                        order={order}
                        onClose={onClose}
                        onStatusChange={onStatusChange}
                        onViewConversation={onViewConversation}
                        onRetryPosPush={onRetryPosPush}
                        paymentActions={paymentActions}
                        onPlaceHeldTransfer={onPlaceHeldTransfer}
                        placingHeldTransfer={placingHeldTransfer}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
